/* eslint-disable no-case-declarations */
import { useState } from 'react';
import cn from 'classnames';
import styles from './Clock.module.scss';
import { useInterval } from '../../utils/useInterval';

interface ClockConfiguration {
  format: 'hh:mm:ss:ms' | 'hh:mm:ss' | 'hh:mm';
}

interface TimerConfiguration {
  /**
     Limit in format HH:MM:SS
  */
  limit: string;
  startFromLimit: boolean;
  initTime?: string;
}

interface ClockI {
  configuration?: ClockConfiguration;
  tool?: 'clock' | 'chronometer' | 'timer';
  timerConfiguration?: TimerConfiguration;
  className?: string;
}

export const Clock = ({
  configuration = { format: 'hh:mm:ss:ms' },
  tool = 'timer',
  timerConfiguration = {
    limit: '02:00:05',
    startFromLimit: false,
    initTime: new Date().toISOString(),
  },
  className,
}: ClockI) => {
  const [time, setTime] = useState<string>(undefined);
  const [initialTime] = useState<Date>(new Date(timerConfiguration.initTime));

  const getRefreshTime = () => {
    switch (configuration.format) {
      case 'hh:mm:ss:ms':
        return 10;
      case 'hh:mm:ss':
        return 1000;
      case 'hh:mm':
        return 60_000;
      default:
        return 60_000;
    }
  };

  const generateNewClock = () => {
    const date = new Date();
    const ms =
      configuration.format === 'hh:mm:ss:ms'
        ? `:${date.getMilliseconds().toString().slice(0, 2)}`
        : '';
    const ss =
      configuration.format === 'hh:mm:ss:ms' ||
      configuration.format === 'hh:mm:ss'
        ? `:${date.getSeconds().toString().padStart(2, '0')}`
        : '';
    const mm =
      configuration.format === 'hh:mm:ss:ms' ||
      configuration.format === 'hh:mm:ss' ||
      configuration.format === 'hh:mm'
        ? `:${date.getMinutes().toString().padStart(2, '0')}`
        : '';
    const requestedTime = `${date
      .getHours()
      .toString()
      .padStart(2, '0')}${mm}${ss}${ms}`;
    setTime(requestedTime);
  };

  const generateNewTimer = () => {
    const date = initialTime;
    const [hh, mm, ss] = timerConfiguration.limit.split(':');
    const hhToMS = Number(hh) * 60 * 60 * 1000;
    const mmToMS = Number(mm) * 60 * 1000;
    const ssToMS = Number(ss) * 1000;
    const finalMS = hhToMS + mmToMS + ssToMS;
    const limitDate = new Date(date.getTime() + finalMS);
    const finalTimeInMS = limitDate.getTime() - Date.now();
    const hours = Math.floor(finalTimeInMS / (1000 * 60 * 60));
    const minutes = Math.floor(finalTimeInMS / (1000 * 60)) % 60;
    const seconds = Math.floor(finalTimeInMS / 1000) % 60;
    const ms =
      configuration.format === 'hh:mm:ss:ms'
        ? `${Math.floor(finalTimeInMS) % 1000}`
        : '';

    setTime(
      `${Number(hours) > 0 ? `${hours}:` : ''}${
        Number(minutes) > 0 || (Number(minutes) === 0 && Number(hours) > 0)
          ? `${minutes.toString().padStart(2, '0')}:`
          : ''
      }${
        Number(seconds) > 0 ||
        (Number(seconds) <= 0 && Number(minutes) <= 0 && Number(hours) <= 0) ||
        (Number(seconds) === 0 && (Number(minutes) > 0 || Number(hours) > 0))
          ? `${
              Number(seconds) <= 0 && Number(minutes) <= 0 && Number(hours) <= 0
                ? '00'
                : seconds.toString().padStart(2, '0')
            }${configuration.format === 'hh:mm:ss:ms' ? ':' : ''}`
          : ''
      }${ms}
      `
    );
  };

  useInterval(() => {
    switch (tool) {
      case 'clock':
        generateNewClock();
        break;
      case 'timer':
        generateNewTimer();
        break;
      default:
        setTime(`${1}`);
        break;
    }
  }, getRefreshTime());

  return <div className={cn(styles.root, className)}>{time}</div>;
};
